import { createFileRoute } from '@tanstack/react-router';
import { INQUIRY_HREF } from '../lib/product-content.js';
import React from 'react';
import { Link } from '@tanstack/react-router';
import { PublicLayout } from '../components/public-layout.js';
import { SectionBlock } from '../components/section-block.js';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ProductRow {
  name: string;
  stage: 'scr' | 'dx' | 'img' | 'int';
  stageLabel: string;
  desc: string;
  href: string;
}

// ─── Credit pool — PASCAL-Home is explicitly excluded ────────────────────────
const PRODUCTS: ProductRow[] = [
  { name: 'MMSE-BeauBrain', stage: 'scr', stageLabel: 'SCR · 선별', desc: '표준화 인지 선별 검사. 1차 선별의 출발점.', href: '/solutions/mmse' },
  { name: 'SCST', stage: 'dx', stageLabel: 'DX · 진단보조', desc: '정식 진단보조 도구. PASCAL-Med 운영 전 단계.', href: '/solutions/scst' },
  { name: 'Amylo', stage: 'img', stageLabel: 'IMG · 영상분석', desc: 'PET 영상 기반 아밀로이드 정량 분석.', href: '/solutions/amylo' },
  { name: 'Morph', stage: 'img', stageLabel: 'IMG · 영상분석', desc: 'MRI 기반 해마·피질 위축 정량 분석.', href: '/solutions/morph' },
  { name: 'PASCAL-Med', stage: 'int', stageLabel: 'INT · 인지중재', desc: '의료기관용 디지털 인지 중재 의료기기 (MZ009).', href: '/solutions/pascal-med' },
];

// stage badge colors (match CSS sol-h-badge vars) — hue 는 옅은 바탕·테두리, fg 는 글자(테마별 진하기, public.css *-fg)
const STAGE_COLOR: Record<string, { hue: string; fg: string }> = {
  scr: { hue: '#3b82f6', fg: 'var(--blue-fg)' },
  dx: { hue: '#22c55e', fg: 'var(--green2-fg)' },
  img: { hue: '#a78bfa', fg: 'var(--violet-fg)' },
  int: { hue: '#f59e0b', fg: 'var(--amber-fg)' },
};

// ─── Components ───────────────────────────────────────────────────────────────
function StageBadge({ stage, label }: { stage: string; label: string }) {
  const c = STAGE_COLOR[stage];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: c?.fg ?? 'var(--ink-3)',
        background: c ? `${c.hue}1a` : 'var(--surface-2)',
        border: `1px solid ${c ? `${c.hue}33` : 'var(--line-2)'}`,
        borderRadius: 4,
        padding: '3px 8px',
      }}
    >
      {label}
    </span>
  );
}

// ─── Journey Step ─────────────────────────────────────────────────────────────
function JourneyStep({
  n, label, sub, detail,
}: { n: number; label: string; sub: string; detail: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        padding: '24px 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'color-mix(in oklab, var(--accent) 10%, transparent)',
          border: '1px solid color-mix(in oklab, var(--accent) 20%, transparent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontFamily: "'Inter Tight', sans-serif",
          fontWeight: 700,
          fontSize: 14,
          color: 'var(--accent)',
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)', marginBottom: 4 }}>
          {label}
          <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 500, color: 'var(--ink-3)' }}>
            {sub}
          </span>
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7 }}>{detail}</div>
      </div>
    </div>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
function Page() {
  return (
    <PublicLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ padding: '160px 0 80px', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <span className="section-label">Unified Credit</span>
          <h1
            className="section-title"
            style={{ marginTop: 12, maxWidth: 700 }}
          >
            통합 크레딧
          </h1>
          <p
            style={{
              color: 'var(--ink-2)',
              fontSize: 18,
              marginTop: 20,
              lineHeight: 1.7,
              maxWidth: 600,
            }}
          >
            진단부터 중재까지, BeauBrain의 모든 솔루션을 하나의 크레딧 풀로. 필요한 검사만, 필요한
            만큼 사용하세요.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 36 }}>
            <a href={INQUIRY_HREF} target="_blank" rel="noopener noreferrer" className="btn-primary btn-lg">
              도입 문의하기 →
            </a>
            <a href="#products" className="btn-outline btn-lg">
              크레딧 구성 보기
            </a>
          </div>
        </div>
      </section>

      {/* ── 환자 한 명을 따라가 봅니다 ────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock
            label="Patient Journey"
            title={<>환자 한 명을<br />따라가 봅니다.</>}
            subtitle="70세 김철수 님. 가족이 최근 건망증을 걱정해 내원했습니다. 통합 크레딧이 이 환자의 진료 전 과정을 어떻게 커버하는지 살펴봅니다."
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 64,
                alignItems: 'start',
                marginTop: 64,
              }}
            >
              {/* story steps */}
              <div>
                <JourneyStep
                  n={1}
                  label="MMSE-BeauBrain으로 초진 선별"
                  sub="SCR"
                  detail="첫 방문 날. 태블릿으로 10분 선별 검사를 진행합니다. MMSE 점수 22/30 — 경도인지장애 의심 범위. 크레딧에서 1건 차감."
                />
                <JourneyStep
                  n={2}
                  label="SCST로 영역별 진단보조"
                  sub="DX"
                  detail="일주일 후 재방문. 주의력·기억력·실행 기능을 영역별로 세분화해 확인합니다. 어떤 인지 영역이 취약한지 정밀하게 파악. 크레딧에서 1건 차감."
                />
                <JourneyStep
                  n={3}
                  label="Amylo / Morph 영상 분석"
                  sub="IMG"
                  detail="PET·MRI 영상이 있다면 아밀로이드 정량·해마 위축 분석으로 생물지표 확인. 임상적 판단의 깊이를 더합니다. 크레딧에서 1건 차감."
                />
                <JourneyStep
                  n={4}
                  label="PASCAL-Med 인지 중재 시작"
                  sub="INT"
                  detail="SCST 결과와 자동 연동되어 주의력·기억력 중심 훈련 패키지 선택. 2~4주 간격 재방문 시마다 재검사 및 진척 확인. 매 세션마다 크레딧에서 1건 차감."
                />
                <JourneyStep
                  n={5}
                  label="가정 인지 활동 안내 (PASCAL-Home)"
                  sub="B2C 안내"
                  detail="의원에서 비의료기기 가정용 앱 PASCAL-Home을 안내합니다. 환자 또는 보호자가 직접 BeauBrain에 구독하며, 의료기관 청구 항목이 아닙니다. 크레딧 미차감."
                />
              </div>

              {/* credit summary card */}
              <div style={{ position: 'sticky', top: 80 }}>
                <div
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--line)',
                    borderRadius: 20,
                    padding: 40,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'var(--ink-3)',
                      marginBottom: 8,
                    }}
                  >
                    — 진료 과정 요약
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: 'var(--ink)' }}>
                    김철수 님의 6개월
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 28 }}>
                    진단 → 중재 → 추적 전 단계를 하나의 크레딧 풀에서
                  </div>
                  {[
                    { step: 'MMSE 선별', count: '1건', stage: 'scr' },
                    { step: 'SCST 진단보조', count: '1건', stage: 'dx' },
                    { step: '영상 분석 (선택)', count: '0~2건', stage: 'img' },
                    { step: 'PASCAL-Med 중재 × 8회', count: '8건', stage: 'int' },
                    { step: 'PASCAL-Home 안내', count: '크레딧 미포함', stage: 'home' },
                  ].map((r) => (
                    <div
                      key={r.step}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: '1px solid var(--line)',
                      }}
                    >
                      <div>
                        <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{r.step}</span>
                        {r.stage === 'home' && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                            환자·보호자 직접 구독 (B2C)
                          </div>
                        )}
                      </div>
                      <span
                        className="num"
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: r.stage === 'home' ? 'var(--ink-3)' : 'var(--accent)',
                        }}
                      >
                        {r.count}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: 16,
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    <span style={{ color: 'var(--ink)' }}>사용 크레딧 합계</span>
                    <span className="num" style={{ color: 'var(--accent)' }}>10~12건</span>
                  </div>
                </div>

                {/* disclaimer: PASCAL-Home excluded */}
                <div
                  style={{
                    marginTop: 16,
                    padding: '14px 18px',
                    background: 'rgba(245,158,11,0.04)',
                    border: '1px solid rgba(245,158,11,0.2)',
                    borderRadius: 10,
                    fontSize: 12,
                    color: 'var(--ink-3)',
                    lineHeight: 1.6,
                  }}
                >
                  ※ PASCAL-Home(비의료기기 웰니스 앱)은 통합 크레딧 풀에 포함되지 않습니다. 환자 또는
                  보호자가 BeauBrain에 직접 구독하며, 의료기관 청구 항목이 아닙니다.
                </div>
              </div>
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 3가지 가치 ─────────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="Why Unified Credit" title="통합 크레딧이 좋은 이유">
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 56 }}
            >
              {[
                {
                  n: '01',
                  title: '필요한 것만 쓰는 유연성',
                  desc: '선별 검사만 많이 쓰는 의원, 영상 분석을 자주 쓰는 병원, 중재를 집중적으로 운영하는 클리닉 — 어떤 운영 패턴에도 동일한 크레딧 풀이 적용됩니다. 특정 솔루션에 묶이지 않습니다.',
                },
                {
                  n: '02',
                  title: '진단 흐름에 따른 자연스러운 연결',
                  desc: '선별 → 진단보조 → 영상 분석 → 중재로 이어지는 임상 흐름이 크레딧 소비 흐름과 정확히 일치합니다. 중간에 필요 없는 단계가 생겨도 크레딧을 낭비하지 않습니다.',
                },
                {
                  n: '03',
                  title: '쓴 만큼 월 정산',
                  desc: '한 달 동안 사용한 크레딧만큼 다음 달에 청구됩니다. 실제 사용 패턴에 맞춰 비용이 정해집니다.',
                },
              ].map((c) => (
                <div
                  key={c.n}
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--line)',
                    borderRadius: 16,
                    padding: '32px 28px',
                  }}
                >
                  <div
                    className="num"
                    style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 16 }}
                  >
                    — VALUE {c.n}
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, lineHeight: 1.3, color: 'var(--ink)' }}
                  >
                    {c.title}
                  </div>
                  <div style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7 }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 크레딧 풀 구성 ───────────────────────────────────────────────────── */}
      <section id="products" style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="Credit Pool" title="크레딧 풀 구성 솔루션">
            <div
              style={{
                marginTop: 48,
                background: 'var(--surface-1)',
                border: '1px solid var(--line)',
                borderRadius: 16,
                overflow: 'hidden',
              }}
            >
              {/* header row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 2fr 80px',
                  padding: '12px 28px',
                  background: 'var(--surface-2)',
                  borderBottom: '1px solid var(--line)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-3)',
                }}
              >
                <span>솔루션</span>
                <span>카테고리</span>
                <span>설명</span>
                <span>크레딧</span>
              </div>

              {PRODUCTS.map((p, i) => (
                <div
                  key={p.name}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 1fr 2fr 80px',
                    padding: '20px 28px',
                    borderBottom: i < PRODUCTS.length - 1 ? '1px solid var(--line)' : 'none',
                    alignItems: 'center',
                    transition: 'background 0.15s',
                  }}
                >
                  <Link
                    to={p.href}
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--ink)',
                      textDecoration: 'none',
                    }}
                  >
                    {p.name}
                  </Link>
                  <div>
                    <StageBadge stage={p.stage} label={p.stageLabel} />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>{p.desc}</div>
                  <div className="num" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                    1건/회
                  </div>
                </div>
              ))}

              {/* PASCAL-Home excluded row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 2fr 80px',
                  padding: '20px 28px',
                  alignItems: 'center',
                  background: 'rgba(245,158,11,0.03)',
                  borderTop: '1px solid var(--line)',
                }}
              >
                <Link
                  to="/solutions/pascal-home"
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: 'var(--ink-3)',
                    textDecoration: 'none',
                  }}
                >
                  PASCAL-Home
                </Link>
                <div>
                  <StageBadge stage="int" label="비의료기기 · B2C" />
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  비의료기기 가정용 인지 활동 앱. 환자·보호자 직접 구독. 의료기관 청구 불가.
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>미포함</div>
              </div>
            </div>

            {/* PASCAL-Home exclusion disclaimer */}
            <div
              style={{
                marginTop: 14,
                padding: '14px 18px',
                background: 'rgba(245,158,11,0.04)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 10,
                fontSize: 12,
                color: 'var(--ink-3)',
                lineHeight: 1.7,
              }}
            >
              ⚠ PASCAL-Home은 비의료기기 웰니스 앱이며 통합 크레딧 풀에 포함되지 않습니다. 환자 또는 보호자가
              BeauBrain에 직접 구독합니다. 의료기관은 PASCAL-Home을 진료비로 청구하거나 환자에게 비용을
              청구할 수 없으며, 안내만 제공합니다. (의료법 제27조·의료기기법 준수)
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 유연성 4가지 ──────────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="Flexibility" title="운영 방식 유연성">
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 56 }}
            >
              {[
                {
                  title: '단일 솔루션 운영 OK',
                  desc: '선별 검사만 운영해도, 영상 분석만 사용해도 됩니다. 특정 솔루션 패키지를 강제하지 않습니다.',
                },
                {
                  title: '크레딧 공유',
                  desc: '의료기관 내 여러 의사가 동일 크레딧 풀을 공유하여 사용할 수 있습니다.',
                },
                {
                  title: '사용량 실시간 확인',
                  desc: '이번 달 사용 크레딧과 예상 청구액을 로그인 후 사용량·청구서 화면에서 언제든 확인할 수 있습니다.',
                },
                {
                  title: '기관 단위 청구',
                  desc: '소속 의사들의 사용분이 합산되어 기관당 매월 청구서 한 장으로 정산됩니다.',
                },
              ].map((f) => (
                <div
                  key={f.title}
                  style={{
                    background: 'var(--surface-1)',
                    border: '1px solid var(--line)',
                    borderRadius: 14,
                    padding: 24,
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: 'var(--ink)' }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 정책 타임라인 (원칙3 면책 포함) ─────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock
            label="Policy Roadmap"
            title="정책 로드맵"
            subtitle="건강보험 급여화와 복지용구 예비급여 관련 정부 검토 동향입니다. 아래 내용은 참고 목적이며, 등재 또는 급여화가 현재 시점에서 보장되지 않습니다."
          >
            <div style={{ maxWidth: 680, margin: '56px auto 0' }}>
              {[
                {
                  year: '2024',
                  label: '비급여 자율 운영',
                  desc: 'PASCAL-Med(MZ009) 비급여 진료 운영 중. 현재 단계.',
                  current: true,
                  warn: false,
                },
                {
                  year: '2025~2026',
                  label: '급여화 준비 단계',
                  desc: '디지털 치료기기 급여화 논의 가속. BeauBrain 임상 데이터 축적 진행 중.',
                  current: false,
                  warn: false,
                },
                {
                  year: '2027 (검토)',
                  label: '건강보험 급여 등재 (정부 검토 단계)',
                  desc: 'PASCAL-Med 급여화 등재 정부 검토 단계. 현시점 등재가 보장되지 않으며 정책 동향에 따라 변동될 수 있습니다.',
                  current: false,
                  warn: true,
                },
                {
                  year: '2028 (검토)',
                  label: '복지용구 예비급여 포함 (정부 검토 단계)',
                  desc: '복지용구 예비급여 포함은 정부 검토 단계이며, 현시점 등재가 보장되지 않습니다. 정책 동향에 따라 변동될 수 있습니다.',
                  current: false,
                  warn: true,
                },
              ].map((t, i, arr) => (
                <div
                  key={t.year}
                  style={{
                    display: 'flex',
                    gap: 24,
                    paddingBottom: i < arr.length - 1 ? 32 : 0,
                    position: 'relative',
                  }}
                >
                  {/* timeline line */}
                  {i < arr.length - 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 19,
                        top: 40,
                        bottom: 0,
                        width: 1,
                        background: 'var(--line)',
                      }}
                    />
                  )}
                  <div style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: t.current
                          ? 'color-mix(in oklab, var(--accent) 15%, transparent)'
                          : t.warn
                          ? 'rgba(245,158,11,0.1)'
                          : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${
                          t.current ? 'color-mix(in oklab, var(--accent) 30%, transparent)' : t.warn ? 'rgba(245,158,11,0.3)' : 'var(--line)'
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        color: t.current ? 'var(--accent)' : t.warn ? '#f59e0b' : 'var(--ink-3)',
                      }}
                    >
                      {t.current ? '●' : '○'}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        className="num"
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: t.current ? 'var(--accent)' : 'var(--ink-3)',
                        }}
                      >
                        {t.year}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                        {t.label}
                      </span>
                      {t.current && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: 'var(--accent)',
                            background: 'color-mix(in oklab, var(--accent) 10%, transparent)',
                            border: '1px solid color-mix(in oklab, var(--accent) 20%, transparent)',
                            borderRadius: 3,
                            padding: '2px 7px',
                          }}
                        >
                          현재
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7 }}>{t.desc}</div>
                    {t.warn && (
                      <div
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          background: 'rgba(245,158,11,0.06)',
                          border: '1px solid rgba(245,158,11,0.2)',
                          borderRadius: 6,
                          fontSize: 12,
                          color: 'var(--amber-fg-soft)',
                        }}
                      >
                        ※ 정부 검토 단계이며 현시점 등재가 보장되지 않습니다. 정책 동향에 따라 변동될 수 있습니다.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── 운영 5단계 ────────────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="How to Start" title="운영 시작 5단계">
            <div style={{ maxWidth: 680, margin: '56px auto 0' }}>
              {[
                { n: 1, step: '문의 및 데모', desc: '상담을 통해 의료기관 운영 패턴과 예상 사용량을 확인합니다.' },
                { n: 2, step: '이용 계약', desc: '사용할 솔루션과 이용 조건을 확인하고 계약합니다.' },
                { n: 3, step: '계정 발급 및 솔루션 연결', desc: 'BeauBrain 담당자가 기관 계정을 만들고 사용할 솔루션을 연결해 드립니다. 관리자가 소속 의사를 초대하면 바로 사용할 수 있습니다.' },
                { n: 4, step: '첫 환자 운영', desc: '솔루션을 사용하면 건별로 크레딧이 차감되고, 사용 내역이 자동으로 집계됩니다.' },
                { n: 5, step: '월 사용량 확인 및 청구서 결제', desc: '사용 크레딧을 로그인 후 사용량 화면에서 확인하고, 매월 발행되는 청구서를 카드·계좌이체·가상계좌로 결제합니다.' },
              ].map((s) => (
                <div
                  key={s.n}
                  style={{
                    display: 'flex',
                    gap: 20,
                    padding: '20px 0',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'color-mix(in oklab, var(--accent) 8%, transparent)',
                      border: '1px solid color-mix(in oklab, var(--accent) 15%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontFamily: "'Inter Tight', sans-serif",
                      fontWeight: 700,
                      fontSize: 13,
                      color: 'var(--accent)',
                    }}
                  >
                    {s.n}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>
                      {s.step}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section style={{ padding: '100px 0', borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <SectionBlock label="FAQ" title="자주 묻는 질문">
            <div style={{ maxWidth: 760, margin: '56px auto 0' }}>
              {[
                {
                  q: '결제는 어떻게 하나요?',
                  a: '한 달 동안 사용한 크레딧만큼 다음 달 초에 청구서가 발행되며, 지급 기한 안에 로그인 후 기관 관리 › 청구서에서 카드·계좌이체·가상계좌로 결제합니다.',
                },
                {
                  q: '크레딧을 미리 구매해 쓸 수도 있나요?',
                  a: '현재는 월 사용량 기준 청구 방식으로 운영합니다. 크레딧을 미리 구매해 사용하는 선불 방식은 추후 추가될 수 있습니다.',
                },
                {
                  q: 'PASCAL-Home도 크레딧으로 사용할 수 있나요?',
                  a: '아닙니다. PASCAL-Home은 비의료기기 웰니스 앱으로 통합 크레딧 풀에 포함되지 않습니다. 환자 또는 보호자가 BeauBrain에 직접 구독하는 B2C 서비스이며, 의료기관이 진료비로 청구하는 항목이 아닙니다.',
                },
                {
                  q: '세금계산서를 받을 수 있나요?',
                  a: '네. 매월 청구서 기준으로 세금계산서를 발행합니다. 청구 내용에 이의가 있으면 결제 전에 고객 지원으로 알려 주세요. 자세한 기준은 환불 정책을 확인하세요.',
                },
                {
                  q: '여러 솔루션을 동시에 운영해야 하나요?',
                  a: '아닙니다. 한 가지 솔루션만 사용해도 크레딧이 적용됩니다. 예를 들어 MMSE-BeauBrain 선별 검사만 운영하거나, PASCAL-Med 중재만 사용해도 됩니다.',
                },
                {
                  q: '크레딧은 여러 의사가 공유할 수 있나요?',
                  a: '네. 의료기관 단위로 크레딧 풀이 부여되며, 의료기관 내 여러 의사가 동일 풀을 공유하여 사용할 수 있습니다.',
                },
                {
                  q: '복지용구 예비급여 등재가 확정되었나요?',
                  a: '아닙니다. 복지용구 예비급여 포함은 정부 검토 단계이며, 현시점 등재가 보장되지 않습니다. 정책 동향에 따라 변동될 수 있습니다.',
                },
              ].map((item) => (
                <div key={item.q} style={{ borderBottom: '1px solid var(--line)', padding: '20px 0' }}>
                  <div
                    style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', color: 'var(--ink)' }}
                  >
                    <span className="num" style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 14 }}>
                      Q.
                    </span>
                    {item.q}
                  </div>
                  <div style={{ marginTop: 10, color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7, paddingLeft: 32 }}>
                    {item.a}
                  </div>
                </div>
              ))}
            </div>
          </SectionBlock>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section
        style={{
          padding: '100px 0',
          textAlign: 'center',
          background: 'var(--surface-1)',
        }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: 42,
              fontWeight: 700,
              color: 'var(--ink)',
              marginBottom: 16,
              letterSpacing: '-0.03em',
            }}
          >
            필요한 만큼, 유연하게.
          </h2>
          <p style={{ color: 'var(--ink-3)', marginBottom: 36, fontSize: 17 }}>
            의료기관 운영 패턴에 맞는 도입 방법을 문의해 주세요.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <a href={INQUIRY_HREF} target="_blank" rel="noopener noreferrer" className="btn-primary btn-lg">
              도입 문의하기 →
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/credit')({
  component: Page,
});
